import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Trash2, CheckCircle, AlertTriangle, XCircle, ChevronRight, Minus, ChevronLeft } from 'lucide-react';
import { Button } from '../components/SubscriptionAppButton';
import { Badge } from '../components/SubscriptionAppBadge';
import { Card } from '../components/SubscriptionAppCard';
import { StatCard } from '../components/StatCard';
// --- Mock Data ---
const IMPORT_RESULTS = [{
  id: 1,
  email: 'sarah.j@example.com',
  name: 'Sarah Johnson',
  plan: 'Reserve Club',
  status: 'Active',
  joinDate: '2023-01-15',
  result: 'Imported',
  reason: null
}, {
  id: 2,
  email: 'mike.smith@test.com',
  name: 'Mike Smith',
  plan: 'Red Only',
  status: 'Active',
  joinDate: '2022-11-01',
  result: 'Merged',
  reason: 'Email already existed — records merged'
}, {
  id: 3,
  email: 'emma.w@winery.com',
  name: 'Emma Williams',
  plan: 'Reserve Club',
  status: 'Active',
  joinDate: '2023-05-20',
  result: 'Imported',
  reason: null
}, {
  id: 4,
  email: 'david.b@example.org',
  name: 'David Brown',
  plan: 'White Only',
  status: 'Cancelled',
  joinDate: '2021-08-15',
  result: 'Imported',
  reason: null
}, {
  id: 5,
  email: 'lisa.k@example.com',
  name: 'Lisa Kim',
  plan: 'Club 6',
  status: 'Active',
  joinDate: '2023-09-01',
  result: 'Imported',
  reason: null
}, {
  id: 6,
  email: 'james.w@test.com',
  name: 'James Wilson',
  plan: 'Reserve Club',
  status: 'Paused',
  joinDate: '2024-01-10',
  result: 'Merged',
  reason: 'Duplicate contact merged'
}, {
  id: 7,
  email: '',
  name: 'Unknown User',
  plan: 'Club 6',
  status: 'Paused',
  joinDate: '2024-03-10',
  result: 'Skipped',
  reason: 'Missing required field: Email'
}, {
  id: 8,
  email: 'bad@format',
  name: 'Invalid Record',
  plan: '-',
  status: '-',
  joinDate: '-',
  result: 'Skipped',
  reason: 'Invalid email format'
}];
const FIELD_MAPPINGS = [{
  source: 'customer_email',
  target: 'Email',
  status: 'mapped'
}, {
  source: 'first_name',
  target: 'First Name',
  status: 'mapped'
}, {
  source: 'last_name',
  target: 'Last Name',
  status: 'mapped'
}, {
  source: 'created_at',
  target: 'Join Date',
  status: 'mapped'
}, {
  source: 'subscription_plan',
  target: 'Subscription Plan',
  status: 'mapped'
}, {
  source: 'status',
  target: 'Status',
  status: 'mapped'
}, {
  source: 'next_billing',
  target: 'Next Billing Date',
  status: 'mapped'
}, {
  source: 'price',
  target: 'Price',
  status: 'mapped'
}, {
  source: 'phone',
  target: 'Phone',
  status: 'mapped'
}, {
  source: 'loyalty_points',
  target: 'Skipped',
  status: 'skipped'
}, {
  source: 'custom_field_1',
  target: 'Skipped',
  status: 'skipped'
}, {
  source: 'referral_code',
  target: 'Skipped',
  status: 'skipped'
}, {
  source: 'internal_notes',
  target: 'Skipped',
  status: 'skipped'
}, {
  source: 'legacy_id',
  target: 'Skipped',
  status: 'skipped'
}];
const TIMELINE_EVENTS = [{
  text: 'Migration completed — 831 records imported successfully',
  time: '12 Jan 2026, 2:34 PM',
  color: 'bg-[#10B981]'
}, {
  text: '4 records skipped due to validation errors',
  time: '12 Jan 2026, 2:34 PM',
  color: 'bg-[#EF4444]'
}, {
  text: '12 duplicate records merged with existing contacts',
  time: '12 Jan 2026, 2:33 PM',
  color: 'bg-[#F59E0B]'
}, {
  text: 'Field mapping confirmed — 9 of 14 columns mapped',
  time: '12 Jan 2026, 2:30 PM',
  color: 'bg-[#3B82F6]'
}, {
  text: 'File uploaded — recharge_export_jan2026.csv (847 rows detected)',
  time: '12 Jan 2026, 2:29 PM',
  color: 'bg-[#3B82F6]'
}, {
  text: 'Migration created by Sarah Chen',
  time: '12 Jan 2026, 2:28 PM',
  color: 'bg-[#3B82F6]'
}];
export function MigrationDetailPage() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [activeTab, setActiveTab] = useState<'All' | 'Warnings' | 'Errors'>('All');
  // Filter logic
  const filteredResults = IMPORT_RESULTS.filter(row => {
    if (activeTab === 'All') return true;
    if (activeTab === 'Warnings') return row.result === 'Merged';
    if (activeTab === 'Errors') return row.result === 'Skipped';
    return true;
  });
  const getResultBadgeVariant = (result: string) => {
    switch (result) {
      case 'Imported':
        return 'success';
      case 'Merged':
        return 'warning';
      case 'Skipped':
        return 'error';
      default:
        return 'default';
    }
  };
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Paused':
        return 'warning';
      case 'Cancelled':
        return 'error';
      default:
        return 'default';
    }
  };
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-10943" data-mp="v5br1ijn3wkm">
      {/* Header Section */}
      <div className="mb-6" data-id="element-10944" data-mp="31vjed8fnxr09">
        <div className="flex items-center gap-2 text-sm text-[#64748B] mb-2" data-id="element-10945" data-mp="31wui5893yyrd">
          <button onClick={() => navigate('/management/migration')} className="hover:text-[#0F172A] transition-colors" data-id="element-10946" data-mp="2wxrn8gmue8x4">
            Migration
          </button>
          <span data-id="element-10947" data-mp="1cha1rdtzfsra">/</span>
          <span data-id="element-10948" data-mp="1hdqy2vbc5dfg">Recharge Subscriber Import</span>
        </div>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-10949" data-mp="1hg3fn7d6llrz">
          <div className="flex items-center gap-3" data-id="element-10950" data-mp="12oukyjuf9a7z">
            <h1 className="text-2xl font-bold text-[#0F172A]" data-id="element-10951" data-mp="17npfgj1r4n7x">
              Recharge Subscriber Import — January 2026
            </h1>
            <Badge variant="success" data-id="element-10952" data-mp="tmv35pbl223z">Completed</Badge>
          </div>
          <div className="flex items-center gap-4" data-id="element-10953" data-mp="ynst4o2u40g1">
            <button onClick={() => console.log('Delete migration')} className="text-sm font-medium text-[#EF4444] hover:text-[#DC2626] transition-colors" data-id="element-10954" data-mp="ylg5buaeavv2">
              Delete Migration
            </button>
            <Button variant="secondary" iconLeft={<Download className="w-4 h-4" data-id="element-10956" data-mp="2e41f6ok82d5m" />} onClick={() => console.log('Download report')} data-id="element-10955" data-mp="or68o0ef7bej">
              Download Report
            </Button>
          </div>
        </div>
      </div>

      {/* Metadata Strip */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg px-6 py-4 mb-8 flex flex-wrap items-center gap-x-8 gap-y-2 text-sm shadow-sm" data-id="element-10957" data-mp="2hn06i6rnvuzg">
        <div className="flex items-center gap-2" data-id="element-10958" data-mp="2cpgs96uyri7i">
          <span className="text-[#64748B]" data-id="element-10959" data-mp="27p2m5ucxaic4">Source:</span>
          <span className="font-medium text-[#0F172A]" data-id="element-10960" data-mp="23mfpng5b498l">Recharge</span>
        </div>
        <div className="w-px h-4 bg-[#E2E8F0] hidden sm:block" data-id="element-10961" data-mp="23ose8c5klhum" />
        <div className="flex items-center gap-2" data-id="element-10962" data-mp="30bdl70kvsb6t">
          <span className="text-[#64748B]" data-id="element-10963" data-mp="2vdlv8c3sqz59">Date:</span>
          <span className="font-medium text-[#0F172A]" data-id="element-10964" data-mp="2ve59niu8n5ej">12 Jan 2026</span>
        </div>
        <div className="w-px h-4 bg-[#E2E8F0] hidden sm:block" data-id="element-10965" data-mp="2qlkiyq6mp8f8" />
        <div className="flex items-center gap-2" data-id="element-10966" data-mp="2mljx4kh79uy5">
          <span className="text-[#64748B]" data-id="element-10967" data-mp="2ml1osph9jq9v">Duration:</span>
          <span className="font-medium text-[#0F172A]" data-id="element-10968" data-mp="3j3g3egd67zj6">4m 32s</span>
        </div>
        <div className="w-px h-4 bg-[#E2E8F0] hidden sm:block" data-id="element-10969" data-mp="3ecpzl7ke84ql" />
        <div className="flex items-center gap-2" data-id="element-10970" data-mp="3eb6en3nsl5ih">
          <span className="text-[#64748B]" data-id="element-10971" data-mp="10uljh7imwutn">File:</span>
          <span className="font-medium text-[#0F172A]" data-id="element-10972" data-mp="10vwp7cvn4cne">
            recharge_export_jan2026.csv
          </span>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-10973" data-mp="19tgnqe24buub">
        <StatCard label="Total Records" value="847" data-id="element-10974" data-mp="1etv028ar5qy5" />
        <StatCard label="Successfully Imported" value="831" icon={<CheckCircle className="w-6 h-6 text-[#10B981]" data-id="element-10976" data-mp="1jrmrz2bedd2c" />} data-id="element-10975" data-mp="1eri8r4j3u48f" />
        <StatCard label="Warnings" value="12" icon={<AlertTriangle className="w-6 h-6 text-[#F59E0B]" data-id="element-10978" data-mp="1oqhjqtrcebyl" />} data-id="element-10977" data-mp="1orslko1899n2" />
        <StatCard label="Errors" value="4" icon={<XCircle className="w-6 h-6 text-[#EF4444]" data-id="element-10980" data-mp="1tjsfnzum98bq" />} data-id="element-10979" data-mp="1tir7c34zizxp" />
      </div>

      {/* Two-Column Row */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-8" data-id="element-10981" data-mp="1xisyvjh0acz9">
        {/* Left: Import Results */}
        <div className="lg:col-span-3" data-id="element-10982" data-mp="22kqmsr0cpvyj">
          <Card padding="none" className="h-full flex flex-col" data-id="element-10983" data-mp="27ia1fs0yoar0">
            <div className="px-6 py-4 border-b border-[#E2E8F0]" data-id="element-10984" data-mp="27ijslixqsnya">
              <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-10985" data-mp="1ie1ywq5f6cln">
                Import Results
              </h2>
            </div>

            {/* Tabs */}
            <div className="px-6 border-b border-[#E2E8F0] flex gap-6" data-id="element-10986" data-mp="1rft2npgeg9cv">
              {[{
              id: 'All',
              label: 'All (847)'
            }, {
              id: 'Warnings',
              label: 'Warnings (12)'
            }, {
              id: 'Errors',
              label: 'Errors (4)'
            }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as any)} className={`py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'border-[#0EA5E9] text-[#0EA5E9]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-10987" data-mp="uq27rp474k6j">
                  {tab.label}
                </button>)}
            </div>

            {/* Table */}
            <div className="flex-1 overflow-x-auto" data-id="element-10988" data-mp="fy9udinypg7g">
              <table className="w-full text-sm text-left" data-id="element-10989" data-mp="by9fxy0v550e">
                <thead className="bg-[#F8FAFC] text-[#64748B] font-medium border-b border-[#E2E8F0]" data-id="element-10990" data-mp="1vdxd2131ky5">
                  <tr data-id="element-10991" data-mp="6lkkexfy4xea">
                    <th className="px-6 py-3 font-semibold" data-id="element-10992" data-mp="6mvxjnva7hi2">Email</th>
                    <th className="px-6 py-3 font-semibold" data-id="element-10993" data-mp="3o2ki7nkr3npn">Name</th>
                    <th className="px-6 py-3 font-semibold" data-id="element-10994" data-mp="3o07s2n4ua71i">Plan</th>
                    <th className="px-6 py-3 font-semibold" data-id="element-10995" data-mp="3t26vfx0kx3yg">Status</th>
                    <th className="px-6 py-3 font-semibold" data-id="element-10996" data-mp="kl2dhp09ywtg">Join Date</th>
                    <th className="px-6 py-3 font-semibold" data-id="element-10997" data-mp="kjr51bvvx0w0">Result</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]" data-id="element-10998" data-mp="ano5mdsf3gcr">
                  {filteredResults.map(row => <tr key={row.id} className="hover:bg-[#F8FAFC] group" data-id="element-10999" data-mp="fmirjololl13">
                      <td className="px-6 py-4" data-id="element-11000" data-mp="folmmn1oy4co">
                        <span className={!row.email ? 'text-[#EF4444] italic' : 'text-[#0F172A]'} data-id="element-11001" data-mp="2vddjanhdww3v">
                          {row.email || 'Missing'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-[#0F172A]" data-id="element-11002" data-mp="166faf5fvivtu">{row.name}</td>
                      <td className="px-6 py-4 text-[#64748B]" data-id="element-11003" data-mp="1b50an4v4lfia">{row.plan}</td>
                      <td className="px-6 py-4" data-id="element-11004" data-mp="wfkmlwmzbo6a">
                        {row.status !== '-' && <Badge variant={getStatusBadgeVariant(row.status)} data-id="element-11005" data-mp="wfawm91xph0l">
                            {row.status}
                          </Badge>}
                      </td>
                      <td className="px-6 py-4 text-[#64748B]" data-id="element-11006" data-mp="ne3a0o8s64mc">
                        {row.joinDate}
                      </td>
                      <td className="px-6 py-4 relative" data-id="element-11007" data-mp="sbwe9bqiwojz">
                        <div className="inline-block cursor-help" data-id="element-11008" data-mp="dhrttn62kumm">
                          <Badge variant={getResultBadgeVariant(row.result)} data-id="element-11009" data-mp="ifjk6cr2g8op">
                            {row.result}
                          </Badge>
                          {row.reason && <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-[#1E293B] text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-11010" data-mp="27sn0ul1lw1na">
                              {row.reason}
                            </div>}
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-between" data-id="element-11011" data-mp="63z8xb49ee4g">
              <span className="text-sm text-[#64748B]" data-id="element-11012" data-mp="jv5hrxtfho40">
                Showing 1-{filteredResults.length} of 847 records
              </span>
              <div className="flex gap-2" data-id="element-11013" data-mp="tuwbvye2u13k">
                <Button variant="secondary" size="sm" disabled iconLeft={<ChevronLeft className="w-4 h-4" data-id="element-11015" data-mp="owbee6q7lvit" />} data-id="element-11014" data-mp="tttkzqgr7ynn">
                  Previous
                </Button>
                <Button variant="secondary" size="sm" iconRight={<ChevronRight className="w-4 h-4" data-id="element-11017" data-mp="2vmrmsbsie1kq" />} data-id="element-11016" data-mp="2vlzo2s3m7nq8">
                  Next
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* Right: Field Mapping */}
        <div className="lg:col-span-2" data-id="element-11018" data-mp="um4mt6van838">
          <Card padding="none" className="h-full" data-id="element-11019" data-mp="ziuyli02fhiu">
            <div className="px-6 py-4 border-b border-[#E2E8F0]" data-id="element-11020" data-mp="zjmu6stnxf94">
              <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-11021" data-mp="13jwzs4djm3ng">
                Field Mapping Used
              </h2>
            </div>
            <div className="divide-y divide-[#E2E8F0]" data-id="element-11022" data-mp="18hyi2n359yxv">
              {FIELD_MAPPINGS.map((mapping, idx) => <div key={idx} className="px-6 py-3 flex items-center justify-between text-sm" data-id="element-11023" data-mp="1diw2l6bmn52j" data-mpi={idx}>
                  <span className="font-mono text-xs bg-[#F1F5F9] px-2 py-1 rounded text-[#475569]" data-id="element-11024" data-mp="1igedh297fud6">
                    {mapping.source}
                  </span>
                  <div className="flex items-center gap-2" data-id="element-11025" data-mp="1r9hkost9bazd">
                    <span className={mapping.status === 'skipped' ? 'text-[#94A3B8]' : 'text-[#0F172A] font-medium'} data-id="element-11026" data-mp="1w9nhsoiappou">
                      {mapping.target}
                    </span>
                    {mapping.status === 'mapped' ? <CheckCircle className="w-4 h-4 text-[#10B981]" data-id="element-11027" data-mp="2bcdojgagvl8g" /> : <Minus className="w-4 h-4 text-[#94A3B8]" data-id="element-11028" data-mp="2bdymzgzwjobl" />}
                  </div>
                </div>)}
            </div>
          </Card>
        </div>
      </div>

      {/* Timeline Row */}
      <Card padding="none" data-id="element-11029" data-mp="21ezlcidk0sy3">
        <div className="px-6 py-4 border-b border-[#E2E8F0]" data-id="element-11030" data-mp="21gaoqh337yoo">
          <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-11031" data-mp="1xegsqm3u7iuu">
            Import Timeline
          </h2>
        </div>
        <div className="p-6" data-id="element-11032" data-mp="2u59ado6f5iyq">
          <div className="relative pl-8 space-y-8 before:absolute before:left-[7px] before:top-2 before:bottom-2 before:w-0.5 before:bg-[#E2E8F0]" data-id="element-11033" data-mp="2p4buji8vt10e">
            {TIMELINE_EVENTS.map((event, idx) => <div key={idx} className="relative flex items-start justify-between group" data-id="element-11034" data-mp="2gbqdc6kwf2do" data-mpi={idx}>
                <div className="flex items-start gap-4" data-id="element-11035" data-mp="2g9xgfj037wiv">
                  <div className={`absolute -left-8 w-2.5 h-2.5 rounded-full mt-1.5 ring-4 ring-white ${event.color}`} data-id="element-11036" data-mp="3cufxroi5qyqv" />
                  <span className="text-sm text-[#0F172A]" data-id="element-11037" data-mp="381mrgw71jico">{event.text}</span>
                </div>
                <span className="text-xs text-[#64748B] whitespace-nowrap ml-4" data-id="element-11038" data-mp="2sjlfaxp8peeo">
                  {event.time}
                </span>
              </div>)}
          </div>
        </div>
      </Card>
    </div>;
}